import type {ChangesetFile} from '@microsoft/agent-host-protocol'
import type {EstablishedAhpSessionView} from '@github-ui/ahp-session-client/contracts'

/** Content the host can measure ahead of any read is skipped once it crosses this threshold, so
 * a metered connection never pays for an oversized file just to render a diff. */
const MAX_CONTENT_BYTES = 5 * 1024 * 1024

export const UNPREVIEWABLE_REASON = {
  BINARY: 'binary',
  UNAVAILABLE: 'unavailable',
} as const

export type UnpreviewableReason = (typeof UNPREVIEWABLE_REASON)[keyof typeof UNPREVIEWABLE_REASON]

export interface ResolvedChangesetFileContent {
  /** Resolved "before" text. Empty string for file creations (no before side to read). `undefined`
   * when a before side exists but its content could not be read/decoded. */
  readonly before: string | undefined
  /** Resolved "after" text. Empty string for file deletions. `undefined` when unreadable. */
  readonly after: string | undefined
  /** True when both sides that should exist were readable as text. False for binary, oversized,
   * unreadable, or unsupported content — callers should fall back to "no preview available". */
  readonly previewable: boolean
  /** Set when `previewable` is false; see {@link UnpreviewableReason}. */
  readonly unpreviewableReason?: UnpreviewableReason
}

interface FileEditSide {
  readonly uri?: string
  readonly content?: {readonly uri?: string; readonly sizeHint?: number; readonly contentType?: string}
}

/** Attempts a strict UTF-8 decode of base64-encoded content, mirroring the artifact workbench's
 * `decodeResourceText` (`components/artifacts/use-artifact-workbench.ts`). Hosts report `encoding:
 * 'base64'` for any content they couldn't send as `'utf-8'`, which includes genuinely binary files
 * but also valid UTF-8 text the host simply chose to base64-encode — only a failed decode means
 * the bytes truly aren't text. */
function decodeBase64Text(data: string): {text: string} | {reason: UnpreviewableReason} {
  let bytes: Uint8Array
  try {
    bytes = Uint8Array.from(atob(data), character => character.charCodeAt(0))
  } catch {
    // Malformed base64 isn't a legible signal either way — we don't know if the file is binary.
    return {reason: UNPREVIEWABLE_REASON.UNAVAILABLE}
  }
  try {
    const text = new TextDecoder('utf-8', {fatal: true}).decode(bytes)
    // NUL is valid UTF-8, so a decode alone won't catch binary content that happens to be a legal
    // byte sequence (e.g. a WASM module's `\0asm` header) — mirrors decodeArtifactResource's check.
    return text.includes('\0') ? {reason: UNPREVIEWABLE_REASON.BINARY} : {text}
  } catch {
    return {reason: UNPREVIEWABLE_REASON.BINARY}
  }
}

async function readSide(
  view: EstablishedAhpSessionView,
  side: FileEditSide | undefined,
  sideShouldExist: boolean,
): Promise<{text: string | undefined; previewable: boolean; reason?: UnpreviewableReason}> {
  // A missing side is expected for creations (no `before`) and deletions (no `after`) — that's an
  // empty file, not an unreadable one.
  if (!sideShouldExist) return {text: '', previewable: true}

  const contentUri = side?.content?.uri
  if (!contentUri) return {text: undefined, previewable: false, reason: UNPREVIEWABLE_REASON.UNAVAILABLE}

  const sizeHint = side?.content?.sizeHint
  if (sizeHint !== undefined && sizeHint > MAX_CONTENT_BYTES) {
    return {text: undefined, previewable: false, reason: UNPREVIEWABLE_REASON.UNAVAILABLE}
  }

  if (!view.readResource) return {text: undefined, previewable: false, reason: UNPREVIEWABLE_REASON.UNAVAILABLE}

  try {
    const result = await view.readResource(contentUri)
    if (result.encoding === 'utf-8') return {text: result.data, previewable: true}
    // Any other encoding is base64 (the protocol's `ContentEncoding` is exhaustive) — decode it
    // before judging it binary, since base64 alone doesn't mean non-text.
    const decoded = decodeBase64Text(result.data)
    if ('text' in decoded) return {text: decoded.text, previewable: true}
    return {text: undefined, previewable: false, reason: decoded.reason}
  } catch {
    return {text: undefined, previewable: false, reason: UNPREVIEWABLE_REASON.UNAVAILABLE}
  }
}

/** Resolves one changeset file's before/after text via `readResource`. Never throws — any
 * failure marks that side not previewable. Reads sequentially to bound caller concurrency. */
export async function resolveChangesetFileContent(
  view: EstablishedAhpSessionView,
  file: ChangesetFile,
): Promise<ResolvedChangesetFileContent> {
  const before = await readSide(view, file.edit.before, !!file.edit.before)
  const after = await readSide(view, file.edit.after, !!file.edit.after)
  const previewable = before.previewable && after.previewable
  // A confirmed binary read on either side wins over an "unavailable" read on the other.
  const unpreviewableReason = previewable
    ? undefined
    : before.reason === UNPREVIEWABLE_REASON.BINARY || after.reason === UNPREVIEWABLE_REASON.BINARY
      ? UNPREVIEWABLE_REASON.BINARY
      : UNPREVIEWABLE_REASON.UNAVAILABLE
  return {
    before: before.text,
    after: after.text,
    previewable,
    ...(unpreviewableReason ? {unpreviewableReason} : {}),
  }
}
